import { ApiError } from "./client.ts";

/**
 * 带组织身份的取数。
 *
 * 🔴 **商家侧每个请求都要带 `X-Org-Id`。**
 *    一个账号可能同时是买家、某经销商的 owner、另一个分销商的员工，
 *    服务端靠这个头判断"他此刻代表谁" —— 猜是猜不出来的。
 *
 * ⚠️ 不复用 `api`（那里没有这个头），但错误处理保持一致：
 *    一律抛 ApiError，页面只认 message，不去猜 HTTP 状态。
 */

const BASE = import.meta.env.VITE_API_BASE ?? "/api";

async function request<T>(
  path: string,
  orgId: number,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: {
      /**
       * ⚠️ **没 body 就别声明 Content-Type。** 声明了 JSON 却发空 body，
       *    Fastify 的 JSON parser 会在进 handler 之前就把请求打回来 ——
       *    页面上看到的是"服务器开小差了"，而 DELETE 正好是没 body 的那一类。
       */
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      "X-Org-Id": String(orgId),
      ...init?.headers,
    },
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { code?: string; message?: string; traceId?: string }
      | null;
    throw new ApiError(
      body?.code ?? "INTERNAL",
      body?.message ?? "网络异常，请稍后重试",
      body?.traceId ?? "",
      res.status,
    );
  }

  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export const orgApi = {
  get: <T>(path: string, orgId: number) => request<T>(path, orgId),
  post: <T>(path: string, orgId: number, body?: unknown) =>
    request<T>(path, orgId, {
      method: "POST",
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  patch: <T>(path: string, orgId: number, body?: unknown) =>
    request<T>(path, orgId, {
      method: "PATCH",
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  del: <T>(path: string, orgId: number) => request<T>(path, orgId, { method: "DELETE" }),
};

/**
 * 裸的商家端 fetch。
 *
 * ⚠️ 跟上面的 `orgApi` 是两套写法：`orgApi` 抛 `ApiError`（带 code / traceId），
 *    这个抛普通 Error，只给 message。几个商家页原本各自抄了一份一模一样的实现，
 *    收在这里一份 —— 抄多了迟早走岔（上面那条 Content-Type 的坑就只修在了几份里）。
 */
export async function orgFetch<T>(path: string, orgId: number, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}/merchant${path}`, {
    credentials: "include",
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      "X-Org-Id": String(orgId),
      ...init?.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.message ?? "请求失败");
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/** 分转元的输入框：商家填的是"元"，库里存的是"分"。 */
export function toCents(yuan: string): number | null {
  const n = Number(yuan);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}
